<template>
  <div class="settings-users-create">
    <div class="page__header">
      <div class="page__title">
        <router-link
          :to="{ name: 'settings-users', params: { id: selectedCompany } }"
          class="page__back-link"
        >
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('settings.users.pages.create-new.title') }}
        </div>
      </div>
    </div>
    <div
      class="settings-users-create__form settings-users-create__form--loading"
      v-if="isRolesLoading"
    >
      <loader></loader>
    </div>
    <div class="settings-users-create__form" v-else>
      <div class="settings-users-create__form-fields">
        <div class="settings-users-create__form-field">
          <text-input
            v-model:value="userData.phone"
            :mask="$options.PHONE_MASK"
            :placeholder="
              $t('settings.users.pages.create-new.fields.phone-placeholder')
            "
            :title="$t('settings.users.pages.create-new.fields.phone')"
          ></text-input>
        </div>
        <div class="settings-users-create__form-field">
          <text-input
            v-model:value="userData.email"
            :title="$t('settings.users.pages.create-new.fields.email')"
            :placeholder="
              $t('settings.users.pages.create-new.fields.email-placeholder')
            "
          ></text-input>
        </div>
        <div class="settings-users-create__form-field">
          <text-input
            v-model:value="userData.firstName"
            :title="$t('settings.users.pages.create-new.fields.firstname')"
            :placeholder="
              $t('settings.users.pages.create-new.fields.firstname-placeholder')
            "
          ></text-input>
        </div>
        <div class="settings-users-create__form-field">
          <text-input
            v-model:value="userData.lastName"
            :title="$t('settings.users.pages.create-new.fields.lastname')"
            :placeholder="
              $t('settings.users.pages.create-new.fields.lastname-placeholder')
            "
          ></text-input>
        </div>
        <div class="settings-users-create__form-field">
          <custom-select
            mode="big"
            :title="$t('settings.users.pages.create-new.fields.role')"
            :placeholder="
              $t('settings.users.pages.create-new.fields.role-placeholder')
            "
            v-model:value="userData.role"
            :options="availableRoles"
            value-key="id"
            :has-default-value="false"
          ></custom-select>
        </div>
      </div>
      <div class="settings-users-create__form-actions">
        <primary-button
          class="settings-users-create__form-action"
          :disabled="v$.$silentErrors.length > 0 || isUserCreating"
          :label="$t('settings.users.pages.create-new.actions.create')"
          @click="createUser"
        ></primary-button>
      </div>
    </div>
    <success-notification
      ref="userCreatedSuccessNotification"
      :text="$t('settings.users.notifications.user-created')"
    ></success-notification>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import Pagination from '@vue-app/components/base/control/Pagination.vue'
import SearchInput from '@vue-app/components/base/input/SearchInput.vue'
import { mapState } from 'vuex'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import UserItem from '@vue-app/components/company/settings/users/UserItem.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

export default {
  name: 'SettingsUserCreate',
  components: {
    SuccessNotification,
    CustomSelect,
    TextInput,
    ArrowButton,
    Loader,
    PrimaryButton,
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      userData: {
        phone: '',
        email: '',
        firstName: '',
        lastName: '',
        role: null,
      },
      isRolesLoading: true,
      isUserCreating: false,

      availableRoles: [],
    }
  },
  validations() {
    return {
      userData: {
        phone: {
          required,
        },
        email: {
          required,
        },
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        role: {
          required,
        },
      },
    }
  },
  PHONE_MASK: '00000 000 00 00',
  computed: {
    ...mapState(['selectedCompany']),
  },
  watch: {},
  created() {
    this.loadRoles()
  },
  methods: {
    async loadRoles() {
      this.isRolesLoading = true

      try {
        let _rolesResponse = await CompaniesApi.getCompanyRoles(
          this.selectedCompany
        )
        this.availableRoles = _rolesResponse.data.map((_role) => ({
          id: _role.role_slug,
          name: `roles.${_role.role_slug}`,
        }))
      } catch (e) {
      } finally {
        this.isRolesLoading = false
      }
    },
    async createUser() {
      this.isUserCreating = true

      let isValid = await this.v$.$validate()

      if (!isValid) {
        this.isUserCreating = false
        return false
      }

      try {
        let _createUserResponse = await CompaniesApi.createCompanyUser(
          this.selectedCompany,
          {
            phone: this.userData.phone,
            email: this.userData.email,
            first_name: this.userData.firstName,
            last_name: this.userData.lastName,
            role: this.userData.role,
          }
        )

        await this.$refs.userCreatedSuccessNotification.show()
        await this.$router.push({
          name: 'settings-users',
          params: {
            ...this.$route.params,
          },
        })
      } catch (e) {
      } finally {
        this.isUserCreating = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-users-create {
  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 60px 50px 74px;
    width: 100%;
    max-width: 390px;
    margin-top: 20px;

    &--loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-fields {
      margin-bottom: 40px;
    }

    &-field {
      margin-bottom: 30px;

      &-hint {
        width: 100%;
        font-weight: 500;
        font-size: 10px;
        line-height: 14px;
        color: #b9c4d8;
        margin-top: 5px;
        flex-wrap: wrap;
      }

      &--row {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        width: calc(100% - 121px);
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }
}
</style>
